import { BaseScene } from './BaseScene';
import { SceneKey } from './sceneKeys';
import { GameMode } from '../types/GameMode';
import { GAME_WIDTH, GAME_HEIGHT } from './gameConfig';
import { SurvivalSaveData, SurvivalStats } from '../types/SurvivalSaveData';
import { saveSurvivalData } from '../utils/survivalManager';
import { playSe } from '../utils/audioManager';
import { AudioKey } from '../constants/audioKeys';
import {
  getSurvivalAiLevel,
  getSurvivalEnemyStage,
  applyBonusPoint,
  SURVIVAL_BONUS_HP_PER_POINT,
  STAT_CAP,
  calcAutoGrowth,
} from '../constants/survivalConfig';
import { getMonsterWithGrownStats } from '../constants/monsterStats';
import { MONSTER_DATABASE } from '../constants/monsters';
import { Monster } from '../types/Monster';
import { checkSurvivalAchievements, unlockAchievements } from '../utils/achievementManager';
import { showAchievementToast } from '../utils/achievementToast';

/** SurvivalExpAllocationSceneに渡されるデータ */
export interface SurvivalExpAllocationSceneData {
  saveData: SurvivalSaveData;
}

type StatKey = keyof SurvivalStats;

const STAT_LABELS: Record<StatKey, string> = {
  hp: 'HP',
  strength: '腕力',
  special: '特殊',
  speed: '素早さ',
  toughness: '丈夫さ',
};

const STAT_KEYS: StatKey[] = ['hp', 'strength', 'special', 'speed', 'toughness'];

/**
 * サバイバルモード 成長・ボーナスポイント割り振り画面
 * 勝利後に自動成長の結果を表示し、ボーナスポイント（3pt）を好きなパラメータへ割り振る
 */
export class SurvivalExpAllocationScene extends BaseScene {
  private saveData!: SurvivalSaveData;
  private pendingStats!: SurvivalStats;
  private remainingPoints = 0;
  /** 今回の自動成長前のステータス（成長表示用） */
  private prevStats!: SurvivalStats;

  private pointsText!: Phaser.GameObjects.Text;
  private statTexts: Partial<Record<StatKey, Phaser.GameObjects.Text>> = {};
  private growthTexts: Partial<Record<StatKey, Phaser.GameObjects.Text>> = {};
  private bonusTexts: Partial<Record<StatKey, Phaser.GameObjects.Text>> = {};
  private bonusAllocation: Record<StatKey, number> = { hp: 0, strength: 0, special: 0, speed: 0, toughness: 0 };

  constructor() {
    super(SceneKey.SURVIVAL_EXP_ALLOCATION);
  }

  create(data?: SurvivalExpAllocationSceneData): void {
    if (!data?.saveData) {
      this.transitionTo(SceneKey.TITLE);
      return;
    }

    this.saveData = data.saveData;
    this.pendingStats = { ...data.saveData.stats };
    this.remainingPoints = data.saveData.bonusPoints;
    this.bonusAllocation = { hp: 0, strength: 0, special: 0, speed: 0, toughness: 0 };

    // 自動成長前のステータスを復元（表示用）
    // recordSurvivalWinで既に成長済みなので、成長分を引いて復元
    const growth = calcAutoGrowth(data.saveData.monsterId, data.saveData.winCount - 1);
    this.prevStats = {
      hp: data.saveData.stats.hp - growth.hp,
      strength: data.saveData.stats.strength - growth.strength,
      special: data.saveData.stats.special - growth.special,
      speed: data.saveData.stats.speed - growth.speed,
      toughness: data.saveData.stats.toughness - growth.toughness,
    };

    this.setupOrientationPrompt();
    this.createUI();

    const newAchievements = unlockAchievements(checkSurvivalAchievements(this.saveData.winCount));
    if (newAchievements.length > 0) showAchievementToast(this, newAchievements);
  }

  private createUI(): void {
    const cx = GAME_WIDTH / 2;

    this.add
      .text(cx, 25, '成長・ボーナス割り振り', { fontSize: '26px', color: '#ffd700' })
      .setOrigin(0.5);

    this.add
      .text(cx, 60, `${this.saveData.winCount}連勝中`, { fontSize: '18px', color: '#ffffff', stroke: '#000000', strokeThickness: 3 })
      .setOrigin(0.5);

    this.pointsText = this.add
      .text(cx, 90, '', { fontSize: '22px', color: '#00ff88' })
      .setOrigin(0.5);
    this.updatePointsText();

    // パラメータ行
    STAT_KEYS.forEach((key, i) => {
      const y = 140 + i * 52;
      this.createStatRow(key, y);
    });

    // 確定ボタン
    const confirmBtn = this.add
      .text(cx, GAME_HEIGHT - 110, '確定して次のバトルへ', {
        fontSize: '22px',
        color: '#ffffff',
        backgroundColor: '#225522',
        padding: { x: 20, y: 10 },
      })
      .setOrigin(0.5)
      .setInteractive({ useHandCursor: true });
    confirmBtn.on('pointerdown', () => { playSe(this.sound, AudioKey.SE_SELECT); this.onConfirm(); });
    confirmBtn.on('pointerover', () => confirmBtn.setStyle({ color: '#ffff00' }));
    confirmBtn.on('pointerout', () => confirmBtn.setStyle({ color: '#ffffff' }));

    // やめる（タイトルへ）ボタン
    const quitBtn = this.add
      .text(cx, GAME_HEIGHT - 55, 'やめる（セーブして中断）', {
        fontSize: '18px',
        color: '#ffffff',
        padding: { x: 10, y: 8 },
        stroke: '#000000',
        strokeThickness: 3,
      })
      .setOrigin(0.5)
      .setInteractive({ useHandCursor: true });
    quitBtn.on('pointerdown', () => { playSe(this.sound, AudioKey.SE_SELECT); this.onQuit(); });
    quitBtn.on('pointerover', () => quitBtn.setStyle({ color: '#ffffff' }));
    quitBtn.on('pointerout', () => quitBtn.setStyle({ color: '#ffffff' }));
  }

  private createStatRow(key: StatKey, y: number): void {
    const cx = GAME_WIDTH / 2;
    const label = STAT_LABELS[key];

    this.add.text(cx - 240, y, label, { fontSize: '20px', color: '#ffffff' }).setOrigin(0, 0.5);

    // 現在値
    const valText = this.add
      .text(cx - 60, y, '', { fontSize: '20px', color: '#ffffff' })
      .setOrigin(0.5);
    this.statTexts[key] = valText;

    // 自動成長表示
    const growthText = this.add
      .text(cx + 10, y - 10, '', { fontSize: '14px', color: '#88ff88' })
      .setOrigin(0, 0.5);
    this.growthTexts[key] = growthText;

    // ボーナス表示
    const bonusText = this.add
      .text(cx + 10, y + 10, '', { fontSize: '14px', color: '#ffdd44' })
      .setOrigin(0, 0.5);
    this.bonusTexts[key] = bonusText;

    // -ボタン
    const minusBtn = this.add
      .text(cx + 120, y, '－', {
        fontSize: '22px',
        color: '#ff8888',
        backgroundColor: '#442222',
        padding: { x: 10, y: 4 },
      })
      .setOrigin(0.5)
      .setInteractive({ useHandCursor: true });
    minusBtn.on('pointerdown', () => this.onMinus(key));

    // +ボタン
    const plusBtn = this.add
      .text(cx + 170, y, '＋', {
        fontSize: '22px',
        color: '#88ff88',
        backgroundColor: '#224422',
        padding: { x: 10, y: 4 },
      })
      .setOrigin(0.5)
      .setInteractive({ useHandCursor: true });
    plusBtn.on('pointerdown', () => this.onPlus(key));

    this.updateStatDisplay(key);
  }

  private onPlus(key: StatKey): void {
    if (this.remainingPoints <= 0) return;
    const cur = this.pendingStats[key];
    if (cur >= STAT_CAP) return;

    this.pendingStats = applyBonusPoint(this.pendingStats, key);
    this.remainingPoints -= 1;
    this.bonusAllocation[key] += 1;
    this.updateStatDisplay(key);
    this.updatePointsText();
  }

  private onMinus(key: StatKey): void {
    if (this.bonusAllocation[key] <= 0) return;

    const gain = key === 'hp' ? SURVIVAL_BONUS_HP_PER_POINT : 1;
    this.pendingStats = { ...this.pendingStats, [key]: this.pendingStats[key] - gain };
    this.remainingPoints += 1;
    this.bonusAllocation[key] -= 1;
    this.updateStatDisplay(key);
    this.updatePointsText();
  }

  private updateStatDisplay(key: StatKey): void {
    const cur = this.pendingStats[key];
    const prev = this.prevStats[key];
    const autoGrowth = this.saveData.stats[key] - prev;

    this.statTexts[key]?.setText(String(cur));

    if (autoGrowth > 0) {
      this.growthTexts[key]?.setText(`自動+${autoGrowth}`);
    } else {
      this.growthTexts[key]?.setText('');
    }

    const bonusCount = this.bonusAllocation[key];
    if (bonusCount > 0) {
      const bonusGain = key === 'hp' ? bonusCount * SURVIVAL_BONUS_HP_PER_POINT : bonusCount;
      this.bonusTexts[key]?.setText(`ボーナス+${bonusGain}`);
    } else {
      this.bonusTexts[key]?.setText('');
    }
  }

  private updatePointsText(): void {
    this.pointsText.setText(`ボーナスポイント: ${this.remainingPoints}`);
  }

  private onConfirm(): void {
    const updatedSave: SurvivalSaveData = {
      ...this.saveData,
      bonusPoints: this.remainingPoints,
      stats: { ...this.pendingStats },
    };
    saveSurvivalData(updatedSave);
    this.transitionTo(SceneKey.BATTLE, this.buildNextBattleData(updatedSave));
  }

  private onQuit(): void {
    const updatedSave: SurvivalSaveData = {
      ...this.saveData,
      bonusPoints: this.remainingPoints,
      stats: { ...this.pendingStats },
    };
    saveSurvivalData(updatedSave);
    this.transitionTo(SceneKey.TITLE);
  }

  private buildNextBattleData(save: SurvivalSaveData): object {
    const enemyId = save.opponentQueue[0];
    const enemyStage = getSurvivalEnemyStage(save.winCount);
    const aiLevel = getSurvivalAiLevel(save.winCount);

    const baseMonster = MONSTER_DATABASE.find((m) => m.id === save.monsterId);
    if (!baseMonster) throw new Error(`Monster ${save.monsterId} not found`);

    const playerMonster: Monster = {
      ...baseMonster,
      stats: {
        ...save.stats,
        specialAttackCount: Math.floor(save.stats.special / 50),
      },
    };

    const enemy = getMonsterWithGrownStats(enemyId, enemyStage);
    if (!enemy) throw new Error(`Enemy ${enemyId} not found`);

    return {
      mode: GameMode.SURVIVAL,
      survivalSaveData: save,
      playerMonster,
      enemyMonster: enemy,
      aiLevel,
    };
  }
}
